import { Component, effect, inject } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { SpinnerService } from './core/services/spinner.service';
import { WorkflowNotifications } from './core/workflow-loading';
import { Header } from './shared/components/header/header';
import { ToastHost } from './shared/components/toast-host/toast-host';
import { filter, map, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthSession } from './core/auth/auth-session';

@Component({
  selector: 'app-root',
  imports: [Header, MatProgressSpinnerModule, RouterOutlet, ToastHost],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  protected readonly spinnerService = inject(SpinnerService);
  protected readonly authSession = inject(AuthSession);
  // to listen to workflow notifications and show toast messages
  private readonly _workflowNotifications = inject(WorkflowNotifications);
  private _router: Router = inject(Router);
  private _wasAuthenticated = this.authSession.isAuthenticated();

  currentUrl: string = '';

  get isLogin(): boolean {
    return this.currentUrl?.includes('login');
  }

  get isChangePassword(): boolean {
    return this.currentUrl?.includes('change-password');
  }

  get isRegistration(): boolean {
    return this.currentUrl?.includes('user-registration');
  }

  constructor() {
    effect(() => {
      const isAuthenticated = this.authSession.isAuthenticated();
      if (this._wasAuthenticated && !isAuthenticated && !this._isAnonymousRoute()) {
        void this._router.navigate(['/login'], {
          queryParams: { returnUrl: this._router.url },
        });
      }
      this._wasAuthenticated = isAuthenticated;
    });

    this._router.events
      .pipe(
        takeUntilDestroyed(),
        filter((event) => event instanceof NavigationEnd),
        map((event) => event.urlAfterRedirects),
        tap((currentUrl) => (this.currentUrl = currentUrl)),
      )
      .subscribe();
  }

  private _isAnonymousRoute(): boolean {
    const path = this._router.url.split('?')[0];
    return (
      path === '/login' || path === '/user-registration' || /^\/change-password\/[^/]+$/.test(path)
    );
  }
}
